/* External dependencies */
import update from 'immutability-helper';

/* Local dependencies */
import { authExceptionMap, AuthFormType, authSchemaMap } from './schema';

export type AuthFields = {
  email?: string;
  password?: string;
  confirmPassword?: string;
  givenName?: string;
  familyName?: string;
  verificationCode?: string;
  newPassword?: string;
  agreedToTerms?: boolean;
  phone?: string;
};

function validateEmail(email: string, exceptionFactory: any): any {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  if (!emailRegex.test(email)) {
    return { $set: exceptionFactory.invalid() };
  }

  return null;
}

function validatePassword(password: string, exceptionFactory: any): any {
  if (password.length < 8) {
    return { $set: exceptionFactory.tooShort() };
  }

  if (!/[A-Z]/.test(password)) {
    return { $set: exceptionFactory.missingUppercase() };
  }

  if (!/[a-z]/.test(password)) {
    return { $set: exceptionFactory.missingLowercase() };
  }

  if (!/\d/.test(password)) {
    return { $set: exceptionFactory.missingNumber() };
  }

  if (!/[@$!%*?&#^()_+\-=[\]{};':"\\|,.<>/]/.test(password)) {
    return { $set: exceptionFactory.missingSpecialChar() };
  }

  return null;
}

function validateVerificationCode(code: string, exceptionFactory: any): any {
  if (code.length !== 6) {
    return { $set: exceptionFactory.invalid() };
  }

  return null;
}

export function getFieldValidationError(
  field: keyof AuthFields,
  value: AuthFields[keyof AuthFields] | null,
  exceptionFactory: any,
): any {
  if (field === 'agreedToTerms') {
    return value === true ? null : { $set: exceptionFactory.required() };
  }

  if (typeof value !== 'string' || value.length === 0) {
    return null;
  }

  switch (field) {
    case 'email':
      return validateEmail(value, exceptionFactory);

    case 'password':
    case 'newPassword':
      return validatePassword(value, exceptionFactory);

    case 'verificationCode':
      return validateVerificationCode(value, exceptionFactory);

    default:
      return null;
  }
}

export function validateAuthFields<T extends { [key: string]: any }>(
  state: T,
  updates: Partial<AuthFields>,
  fieldsKey: string,
) {
  const field = Object.keys(updates)[0] as keyof AuthFields;

  if (!field) {
    return state;
  }

  const value = updates[field];
  const currentFields = (state as any)[fieldsKey] || {};
  const exceptionFactory = authExceptionMap[field as keyof typeof authExceptionMap];

  const change: any = {};
  const errorField = `${field}Error`;
  const validationError = getFieldValidationError(field, value, exceptionFactory);

  if (validationError) {
    change[errorField] = validationError;
  } else {
    change['$unset'] = [errorField];
  }

  return update(state, {
    ...change,
    [fieldsKey]: {
      $set: {
        ...currentFields,
        [field]: value,
      },
    },
  });
}

export function validateAllAuthFields(
  fields: AuthFields,
  formType: AuthFormType,
): {
  isValid: boolean;
  errors: Record<string, Error | undefined>;
} {
  const errors: any = {};
  const schema = authSchemaMap[formType];

  const result = schema.safeParse(fields);

  if (!result.success) {
    for (const issue of result.error.issues) {
      const field = issue.path[0] as keyof typeof authExceptionMap;
      const reason = issue.message;

      const exceptionFactory = authExceptionMap[field]?.[reason as keyof (typeof authExceptionMap)[typeof field]];

      if (exceptionFactory && !errors[field]) {
        errors[field] = exceptionFactory();
      }
    }
  }

  if (formType === 'register' || formType === 'forgotPasswordReset') {
    const passwordField = formType === 'forgotPasswordReset' ? 'newPassword' : 'password';
    if (fields.confirmPassword && fields[passwordField] !== fields.confirmPassword) {
      errors.confirmPassword = authExceptionMap.confirmPassword.mismatch();
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
}
